import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  min-height: calc(100vh - 80px);
  padding: 4rem 2rem;
  margin-top: 80px;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.1) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
  }
`;

const PricingHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 3rem 0;
`;

const ToggleLabel = styled.span`
  color: ${props => props.active ? '#2563eb' : '#64748b'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const ToggleButton = styled.button`
  position: relative;
  width: 56px;
  height: 28px;
  border-radius: 14px;
  background: ${props => props.active ? '#2563eb' : '#e2e8f0'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '30px' : '2px'};
    width: 24px;
    height: 24px;
    border-radius: 12px;
    background: white;
    transition: all 0.3s ease;
  }
`;

const SaveBadge = styled.span`
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

const PricingCard = styled(motion.div)`
  padding: 3rem 2.5rem;
  background: white;
  border-radius: 24px;
  box-shadow: ${props => props.popular ? '0 8px 16px rgba(37, 99, 235, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.05)'};
  border: 1px solid ${props => props.popular ? 'rgba(37, 99, 235, 0.2)' : 'rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  ${props => props.popular && `
    transform: scale(1.05);
    
    @media (max-width: 1024px) {
      transform: scale(1);
    }
  `}

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(37, 99, 235, 0.1);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  right: -2rem;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 0.5rem 3rem;
  transform: rotate(45deg);
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1e293b;
`;

const PlanDescription = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  min-height: 3.2rem;
`;

const PlanPrice = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;

  .amount {
    font-size: 3.5rem;
    font-weight: 800;
    color: #1e293b;
    line-height: 1;

    .currency {
      font-size: 2rem;
      font-weight: 600;
      vertical-align: super;
    }
  }

  .period {
    color: #64748b;
    font-size: 1rem;
    margin-top: 0.5rem;
  }
`;

const FeatureList = styled.ul`
  margin-bottom: 2rem;
  list-style: none;
  padding: 0;
`;

const Feature = styled.li`
  margin: 1rem 0;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    color: ${props => props.included ? '#2563eb' : '#cbd5e1'};
  }
`;

const CTAButton = styled(motion.button)`
  width: 100%;
  background: ${props => props.primary ? 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#2563eb'};
  padding: 1rem 2rem;
  border-radius: 50px;
  border: ${props => props.primary ? 'none' : '2px solid #2563eb'};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? '0 8px 16px rgba(37, 99, 235, 0.2)' : 'none'};
  }
`;

const GuaranteeSection = styled.div`
  text-align: center;
  margin-top: 6rem;
  position: relative;
  z-index: 1;
`;

const GuaranteeTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const GuaranteeText = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;

const FAQSection = styled.section`
  max-width: 800px;
  margin: 6rem auto 0;
  position: relative;
  z-index: 1;
`;

const FAQTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 3rem;
`;

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  p {
    color: #64748b;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

function Pricing() {
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      name: 'Growth',
      description: 'Perfect for growing Shopify stores looking to optimize their testing.',
      monthlyPrice: 499,
      annualPrice: 449,
      popular: false,
      features: [
        'Up to 10K monthly orders',
        'Checkout & payment testing (weekly)',
        'Mobile & cross-browser testing',
        'Speed & performance monitoring',
        'Basic security scanning',
        'Email support',
        'Weekly reports',
        'Basic API testing'
      ]
    },
    {
      name: 'Scale',
      description: 'For established stores that need comprehensive testing solutions.',
      monthlyPrice: 999,
      annualPrice: 899,
      popular: true,
      features: [
        '10K – 50K monthly orders',
        'Daily checkout & payment testing',
        'Advanced security testing',
        'Third-party integration testing',
        'Dedicated account manager',
        'Priority support',
        'Custom test scenarios',
        'Real-time alerts',
        'API monitoring & testing',
        'Performance optimization'
      ]
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for high-volume Shopify Plus stores.',
      monthlyPrice: null,
      annualPrice: null,
      popular: false,
      features: [
        'Unlimited orders',
        'Continuous monitoring',
        'Custom API testing',
        'Advanced security suite',
        'Custom integrations',
        'Dedicated testing team',
        'VIP support',
        'Custom reporting',
        'SLA guarantee',
        'Custom solutions'
      ]
    }
  ];

  const faqs = [
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and can arrange bank transfers for enterprise clients. All payments are processed securely through Stripe.'
    },
    {
      question: 'Can I change my plan later?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.'
    },
    {
      question: 'Is there a minimum contract period?',
      answer: 'No, our services are provided on a month-to-month basis. Annual plans offer a discount but can be cancelled with pro-rata refunds.'
    },
    {
      question: 'Do you offer a free trial?',
      answer: 'Yes, we offer a 14-day free trial on our Growth and Scale plans. Enterprise plans include a comprehensive demo and pilot period.'
    }
  ];

  return (
    <PageContainer>
      <PricingHeader>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Simple, Transparent Pricing
        </Title>
        <Subtitle>
          Choose the perfect plan for your store. All plans include our core testing features
          with no hidden fees.
        </Subtitle>
        <BillingToggle>
          <ToggleLabel active={!isAnnual}>Monthly</ToggleLabel>
          <ToggleButton
            active={isAnnual}
            onClick={() => setIsAnnual(!isAnnual)}
          />
          <ToggleLabel active={isAnnual}>Annual</ToggleLabel>
          <SaveBadge>Save 10%</SaveBadge>
        </BillingToggle>
      </PricingHeader>

      <PricingGrid>
        {plans.map((plan, index) => (
          <PricingCard
            key={index}
            popular={plan.popular}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {plan.popular && <PopularBadge>Most Popular</PopularBadge>}
            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>
            <PlanPrice>
              {plan.monthlyPrice ? (
                <>
                  <div className="amount">
                    <span className="currency">£</span>
                    {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </div>
                  <div className="period">per month, billed {isAnnual ? 'annually' : 'monthly'}</div>
                </>
              ) : (
                <>
                  <div className="amount">Custom</div>
                  <div className="period">Contact us for pricing</div>
                </>
              )}
            </PlanPrice>
            <FeatureList>
              {plan.features.map((feature, i) => (
                <Feature key={i} included>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {feature}
                </Feature>
              ))}
            </FeatureList>
            <CTAButton
              primary={plan.popular}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {plan.monthlyPrice ? 'Get Started' : 'Contact Sales'}
            </CTAButton>
          </PricingCard>
        ))}
      </PricingGrid>

      <GuaranteeSection>
        <GuaranteeTitle>14-Day Money-Back Guarantee</GuaranteeTitle>
        <GuaranteeText>
          Try our service risk-free. If you're not completely satisfied within your first 14 days,
          we'll refund your payment in full – no questions asked.
        </GuaranteeText>
      </GuaranteeSection>

      <FAQSection>
        <FAQTitle>Frequently Asked Questions</FAQTitle>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <h4>{faq.question}</h4>
            <p>{faq.answer}</p>
          </FAQItem>
        ))}
      </FAQSection>
    </PageContainer>
  );
}

export default Pricing; 